import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import {useParams} from "react-router-dom";
import {Container} from "components/misc/Layouts";
import tw from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";

import researchList from "../researchList.json";
import styled from "styled-components";
import {Helmet} from "react-helmet";
import {PrimaryButton as PrimaryButtonBase} from "../components/misc/Buttons";

const ContentWithPaddingLg = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const HeadingRow = tw.div`flex`;
const Heading = tw.h2`text-2xl font-black tracking-wide text-gray-900`;
const Subheading = tw.h3`font-bold text-primary-500 mt-12 text-xl`

const Description = styled.div`
    ${tw`mt-6 text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`}
    p {
        font-size: 1rem;
    }
    li {
        font-size: 1rem;
    }
    h2 {
        font-size: 1.25rem;
    }
    ol,
    ul {
        padding-top: 10px;
    }
    li span {
        font-size: 1.125rem;
    }
`;

const Button = styled(PrimaryButtonBase)`
    ${tw`rounded-full mt-8 uppercase tracking-wider py-4 text-sm hover:shadow-xl transform hocus:translate-x-px hocus:-translate-y-px focus:shadow-outline`}
`;

const ImageGrid = tw.div`mt-10 grid grid-cols-1 md:grid-cols-3 gap-8`;

const GridImage = styled.img`
  ${tw`w-full h-auto rounded-lg shadow-lg`}
`;

const PaperHeader = styled.div`
  ${tw`text-center border-b border-gray-200 pb-8 mb-8`}
`;

const PaperTitle = styled.h1`
  ${tw`text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-6`}
`;

const PaperMeta = styled.div`
  ${tw`flex flex-wrap justify-center items-center gap-6 text-sm text-gray-600 mb-4`}
  span { font-family: Poppins; }
`;

const BadgeRow = styled.div`
  ${tw`flex justify-center gap-4 mb-6`}
`;

const Badge = styled.span`
  ${tw`flex items-center gap-1 text-sm font-semibold`}
  color: ${props => props.color || '#111'};
`;

const CitationBox = styled.div`
  ${tw`text-sm text-gray-700 font-bold max-w-2xl mx-auto`}
  font-family: Poppins;
`;

export default () => {
    const { slug } = useParams();

    const post = researchList.find(a => a.slug === slug);

    if (!post) {
        return (
            <HeadingRow>
                <Heading>404 Research Paper not found</Heading>
            </HeadingRow>
        );
    }

    return (
        <>
            <Helmet>
                <title>{post.title} | MAI Motion Research</title>
                <meta name="description" content={post.shortDescription} />
                <meta name="keywords" content="3D motion analysis research, biomechanics study, MRI technology, musculoskeletal research, MAI Motion" />
                <link rel="canonical" href={`https://maimotion.com/research/${slug}`} />
                <meta property="og:title" content={`${post.title} | MAI Motion Research`} />
                <meta property="og:description" content={post.shortDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://maimotion.com/research/${slug}`} />
                <meta property="og:image" content={post.image} />
                <meta name="twitter:title" content={`${post.title} | MAI Motion Research`} />
                <meta name="twitter:description" content={post.shortDescription} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image" content={post.image} />
                <meta name="robots" content="index,follow" />
                <meta name="author" content="MAI Motion Research Team" />
                <meta name="article:published_time" content={post.date} />
                <meta name="article:section" content="Research" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "ScholarlyArticle",
                        "headline": post.title,
                        "description": post.shortDescription,
                        "image": post.image,
                        "datePublished": post.date,
                        "url": `https://maimotion.com/research/${slug}`,
                        "publisher": {
                            "@type": "Organization",
                            "name": "MAI Motion",
                            "url": "https://maimotion.com",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://maimotion.com/logo512.png"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://maimotion.com/research/${slug}`
                        },
                        "about": [
                            "3D Motion Analysis",
                            "Biomechanics",
                            "Markerless Motion Capture",
                            "Musculoskeletal Research"
                        ]
                    })}
                </script>
            </Helmet>
            <AnimationRevealPage>
                <Header />
                <Container>
                    <ContentWithPaddingLg>
                        {post.publishedDate ? (
                            <PaperHeader>
                                <PaperTitle>{post.title}</PaperTitle>
                                <Description>
                                    <div dangerouslySetInnerHTML={{ __html: post.authors }} />
                                </Description>
                                <PaperMeta>
                                    <span><span className="font-semibold">Published:</span> {post.publishedDate}</span>
                                    <span><span className="font-semibold">DOI:</span> {post.doi}</span>
                                </PaperMeta>
                                <BadgeRow>
                                    {post.openAccess && (
                                        <Badge color="#f97316">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3.1-9H8.9V6a3.1 3.1 0 0 1 6.2 0v2z"/></svg>
                                            Open Access
                                        </Badge>
                                    )}
                                    {post.peerReviewed && (
                                        <Badge color="#6b7280">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                                            Peer-Reviewed
                                        </Badge>
                                    )}
                                </BadgeRow>
                                {post.citation && (
                                    <CitationBox>
                                        Cite this article as: {post.citation}
                                    </CitationBox>
                                )}
                            </PaperHeader>
                        ) : (
                            <>
                                <HeadingRow>
                                    <Heading>{post.title}</Heading>
                                </HeadingRow>
                                <Description>
                                    <div dangerouslySetInnerHTML={{ __html: post.authors }} />
                                </Description>
                            </>
                        )}
                        {/* Image Grid */}
                        {post.images && post.images.length > 0 && (
                            <ImageGrid>
                                {post.images.map((src, index) => (
                                    <GridImage
                                        key={index}
                                        src={"https://d3ekn1do43nbxb.cloudfront.net/research-papers/images/"+ src}
                                        alt={`Research Image ${index + 1}`}
                                    />
                                ))}
                            </ImageGrid>
                        )}
                        <Subheading>
                            Highlight
                        </Subheading>
                        <Description>
                            <div dangerouslySetInnerHTML={{ __html: post.html }} />
                        </Description>



                        <Button onClick={() => window.open(post.pdf, "_blank")}>
                            Download .PDF
                        </Button>
                    </ContentWithPaddingLg>
                </Container>
                <Footer />
            </AnimationRevealPage>
        </>
    );
};
